"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Heart, Sun, Sparkles, Flame, Zap, Users } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function FiveOfWandsPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-red-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Rider-Waite</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Minör Arkana</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Değnekler</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Beş Değnek</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            AXTROLOG Nedir
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Rider-Waite
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite/minor-arcana"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Minör Arkana
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <span className="text-slate-300">Beş Değnek</span>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-red-900/20 to-orange-900/20 rounded-xl p-8 mb-8 border border-red-500/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/tarot/cards/five-of-wands.svg"
                  alt="Five of Wands Tarot Card"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-700 rounded-full flex items-center justify-center">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">Five of Wands</h1>
                  <p className="text-xl text-red-400">Beş Değnek / Çatışma ve Rekabet</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Çatışma, rekabet ve farklı görüşlerin çarpıştığı durumları temsil eder.
                Yapıcı tartışmalar ve sağlıklı rekabet yoluyla büyüme fırsatları sunar.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-sm">Çatışma</span>
                <span className="px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-sm">Rekabet</span>
                <span className="px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-sm">Tartışma</span>
                <span className="px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-sm">Mücadele</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Sun className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Sağlıklı Rekabet</h3>
                  <p className="text-slate-400 text-sm">Yapıcı yarış, motivasyonel rekabet ve gelişim odaklı mücadele.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Çeşitlilik ve Tartışma</h3>
                  <p className="text-slate-400 text-sm">Farklı fikirler, zengin perspektifler ve beyin fırtınası.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Enerji ve Canlılık</h3>
                  <p className="text-slate-400 text-sm">Yüksek enerji, dinamizm ve aktif katılım.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Yaratıcı Çözümler</h3>
                  <p className="text-slate-400 text-sm">Zorluklar yoluyla innovasyon ve yeni yaklaşımlar.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Yıkıcı Çatışma</h3>
                  <p className="text-slate-400 text-sm">Gereksiz kavgalar, ego savaşları ve zarar verici tartışmalar.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">İç Çatışma</h3>
                  <p className="text-slate-400 text-sm">Kendi içinde bölünmüşlük, kararsızlık ve iç savaş.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Çatışmadan Kaçınma</h3>
                  <p className="text-slate-400 text-sm">Pasiflik, sorunları görmezden gelme ve kaçıngan davranış.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Enerji Dağılması</h3>
                  <p className="text-slate-400 text-sm">Odaksızlık, çok yönlü dağılım ve etkisizlik.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Sembolik Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <Users className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Beş Kişi</h3>
              <p className="text-slate-400 text-sm">Farklı görüşler, çeşitlilik ve grup dinamiği.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <Flame className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Yüksek Değnekler</h3>
              <p className="text-slate-400 text-sm">Savunma, mücadele ve aktif katılım.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">☁️</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Açık Gökyüzü</h3>
              <p className="text-slate-400 text-sm">Açık iletişim, şeffaflık ve dürüstlük.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🏞️</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Açık Alan</h3>
              <p className="text-slate-400 text-sm">Eşit koşullar, adil rekabet ve fırsat eşitliği.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">⚡</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Dinamik Hareket</h3>
              <p className="text-slate-400 text-sm">Enerji, eylem ve aktif katılım.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🎯</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Merkez Odaklılık</h3>
              <p className="text-slate-400 text-sm">Ortak hedef, odaklanma ve yönelim.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-400" />
                Aşk ve İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• İlişkide farklı görüşler</li>
                <li>• Sağlıklı tartışmalar</li>
                <li>• Rekabet içeren durumlar</li>
                <li>• Çift arasında gerilim</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• İş yerinde rekabet</li>
                <li>• Proje tartışmaları</li>
                <li>• Takım çatışmaları</li>
                <li>• Yaratıcı beyin fırtınası</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Sun className="h-5 w-5 text-green-400" />
                Kişisel Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• İç çatışmaları çözme</li>
                <li>• Farklı benlik yönleri</li>
                <li>• Karar verme zorluğu</li>
                <li>• Çeşitlilik zenginliği</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Zap className="h-5 w-5 text-blue-400" />
                Sağlık ve Yaşam
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Stres yönetimi</li>
                <li>• Enerji dağılımı</li>
                <li>• Aktif yaşam tarzı</li>
                <li>• Grup aktiviteleri</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Card Advice */}
        <div className="bg-gradient-to-r from-red-900/20 to-orange-900/20 rounded-xl p-6 mb-8 border border-red-500/20">
          <h3 className="text-lg font-semibold text-red-200 mb-3">💡 Kart Tavsiyesi</h3>
          <p className="text-slate-200 leading-relaxed">
            Çatışmalar ve rekabet durumlarında sakin kalın. Farklı görüşleri dinleyin ve yapıcı
            tartışmalara açık olun. Her çatışma bir öğrenme ve büyüme fırsatıdır. Ego yerine
            ortak iyiyi düşünerek hareket edin.
          </p>
        </div>

        {/* Important Note */}
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
          <p className="text-amber-100 leading-relaxed">
            Bu kart anlamları geleneksel tarot yorumlarına dayanmaktadır ve sadece eğlence ve kişisel farkındalık amaçlıdır.
            Yaşamsal kararlar için uzman görüşü alınmalıdır.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/rider-waite/minor-arcana/wands/four-of-wands"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Dört Değnek
          </Link>

          <Link
            href="/about/tarot/systems/rider-waite/minor-arcana/wands/six-of-wands"
            className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            Altı Değnek
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}